import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form, Image, Modal, Table } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import {
  CustomDropdownContainer,
  DeleteModal,
  FormFieldLabel,
  PageHeader,
  StatusBadge,
  TableSearchField,
} from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  ELIGIBILITY_OPTIONS,
  FREQUENCY_OPTIONS,
  STRATEGY_OPTIONS,
  WORKFLOW_OPTIONS,
  createPolicy,
  deletePolicy,
  fetchAppraisalTypeOptions,
  fetchPolicies,
  fetchPolicyById,
  getErrorMessage,
  updatePolicy,
} from "../pafEngineApi";
import "../PafEngine.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const emptyForm = {
  appraisal_type_id: null,
  frequency: "quarterly",
  generation_strategy: "company_schedule",
  eligibility_type: "both",
  workflow_type: "one_level",
  auto_generate: true,
};

const labelFromOptions = (options, value, fallback = "—") =>
  options.find((o) => o.value === value)?.label || fallback;

const strategyLabel = (value) =>
  value
    ? labelFromOptions(STRATEGY_OPTIONS, value, value)
    : "Inherit (Global)";

const selectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  classNames: {
    menu: () => "paf-settings-select-menu",
  },
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  },
};

const PafEnginePolicies = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [rows, setRows] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [usage, setUsage] = useState(null);

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      const [policies, types] = await Promise.all([
        fetchPolicies(objectViewedId),
        fetchAppraisalTypeOptions(objectViewedId),
      ]);
      setRows(policies);
      setTypeOptions(types);
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.appraisal_type?.name, row.frequency, row.eligibility_type, row.workflow_type]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, filter]);

  const openCreate = () => {
    setEditingId(null);
    setUsage(null);
    setForm(emptyForm);
    setModal("edit");
  };

  const openEdit = async (row) => {
    setEditingId(row.id);
    setForm({
      appraisal_type_id: row.appraisal_type_id,
      frequency: row.frequency,
      generation_strategy: row.generation_strategy || "company_schedule",
      eligibility_type: row.eligibility_type || "both",
      workflow_type: row.workflow_type || "one_level",
      auto_generate: !!row.auto_generate,
    });
    setUsage(null);
    setModal("edit");
    try {
      const detail = await fetchPolicyById(row.id, objectViewedId);
      setUsage(detail?.usage || null);
    } catch (error) {
      // Non-blocking — edit modal still works without usage.
    }
  };

  const openDuplicate = (row) => {
    setEditingId(null);
    setUsage(null);
    setForm({
      appraisal_type_id: row.appraisal_type_id,
      frequency: row.frequency,
      generation_strategy: row.generation_strategy || "company_schedule",
      eligibility_type: row.eligibility_type || "both",
      workflow_type: row.workflow_type || "one_level",
      auto_generate: !!row.auto_generate,
    });
    setModal("edit");
  };

  const save = async () => {
    dispatch(showCommonLoader());
    try {
      const payload = {
        appraisal_type_id: form.appraisal_type_id,
        frequency: form.frequency,
        generation_strategy: form.generation_strategy,
        eligibility_type: form.eligibility_type,
        workflow_type: form.workflow_type,
        auto_generate: !!form.auto_generate,
      };
      if (editingId) {
        await updatePolicy(editingId, payload, objectViewedId);
      } else {
        await createPolicy(payload, objectViewedId);
      }
      setModal(null);
      await load();
      dispatch(notify("Policy saved.", "success"));
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const toggleDisable = async (row) => {
    dispatch(showCommonLoader());
    try {
      await updatePolicy(
        row.id,
        { auto_generate: !row.auto_generate },
        objectViewedId
      );
      await load();
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    dispatch(showCommonLoader());
    try {
      await deletePolicy(deleteId, objectViewedId);
      setDeleteId(null);
      await load();
      dispatch(notify("Policy deleted.", "success"));
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  return (
    <div className="paf-settings-page">
      <PageHeader title="Generation Policies">
        <button type="button" className="add-new-btn" onClick={openCreate}>
          Add Policy
        </button>
      </PageHeader>

      <div className="main-content-div page-card">
        <TableSearchField
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-3"
        />
        <Table responsive>
          <thead>
            <tr>
              <th>Appraisal Type</th>
              <th>Frequency</th>
              <th>Strategy</th>
              <th>Eligibility</th>
              <th>Workflow</th>
              <th>Auto</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8}>No policies found.</td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id}>
                  <td>{row.appraisal_type?.name || "—"}</td>
                  <td>
                    {labelFromOptions(FREQUENCY_OPTIONS, row.frequency, row.frequency)}
                  </td>
                  <td>{strategyLabel(row.generation_strategy)}</td>
                  <td>
                    {labelFromOptions(
                      ELIGIBILITY_OPTIONS,
                      row.eligibility_type,
                      row.eligibility_type
                    )}
                  </td>
                  <td>
                    {labelFromOptions(
                      WORKFLOW_OPTIONS,
                      row.workflow_type,
                      row.workflow_type
                    )}
                  </td>
                  <td>{row.auto_generate ? "Yes" : "No"}</td>
                  <td>
                    <StatusBadge
                      label={row.is_active === "y" || row.is_active === true ? "Active" : "Inactive"}
                      variant={
                        row.is_active === "y" || row.is_active === true
                          ? "active"
                          : "inactive"
                      }
                    />
                  </td>
                  <td className="form-action-width text-end">
                    <Dropdown align="end" className="d-inline-block">
                      <Dropdown.Toggle
                        variant="none"
                        className="border-0 paf-engine-row-actions-toggle"
                        id={`policy-actions-${row.id}`}
                        title="More actions"
                      >
                        ⋮
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => openDuplicate(row)}>
                          Duplicate
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => toggleDisable(row)}>
                          {row.auto_generate
                            ? "Disable Auto Generate"
                            : "Enable Auto Generate"}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <Button
                      variant="none"
                      className="border-0"
                      title="Edit"
                      onClick={() => openEdit(row)}
                    >
                      <Image src="/assets/images/edit-icon.svg" alt="Edit" />
                    </Button>
                    <Button
                      variant="none"
                      className="border-0"
                      title="Delete"
                      onClick={() => setDeleteId(row.id)}
                    >
                      <Image src="/assets/images/delete-icon.svg" alt="Delete" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal
        show={modal === "edit"}
        onHide={() => setModal(null)}
        centered
        className="paf-settings-page"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Edit Policy" : "Create Policy"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingId && usage ? (
            <div className="paf-engine-usage">
              <strong>Used By</strong>
              <div>{usage.appraisal_setups ?? 0} Appraisal Setups</div>
              <div>{usage.generated_pafs ?? 0} Generated PAFs</div>
              <div>{usage.log_rows ?? 0} Generation Log Rows</div>
            </div>
          ) : null}
          <FormFieldLabel>Appraisal Type</FormFieldLabel>
          <Select
            className="multi-select mb-2"
            classNamePrefix="react-select"
            options={typeOptions}
            value={typeOptions.find((o) => o.value === form.appraisal_type_id) || null}
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, appraisal_type_id: opt?.value || null }))
            }
            components={{ ValueContainer: CustomDropdownContainer }}
            {...selectMenuProps}
          />
          <FormFieldLabel>Frequency</FormFieldLabel>
          <Select
            className="multi-select mb-2"
            classNamePrefix="react-select"
            options={FREQUENCY_OPTIONS}
            value={FREQUENCY_OPTIONS.find((o) => o.value === form.frequency)}
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, frequency: opt?.value || "quarterly" }))
            }
            components={{ ValueContainer: CustomDropdownContainer }}
            {...selectMenuProps}
          />
          <FormFieldLabel>Generation Strategy</FormFieldLabel>
          <Select
            className="multi-select mb-2"
            classNamePrefix="react-select"
            options={STRATEGY_OPTIONS}
            value={STRATEGY_OPTIONS.find((o) => o.value === form.generation_strategy)}
            onChange={(opt) =>
              setForm((prev) => ({
                ...prev,
                generation_strategy: opt?.value || "company_schedule",
              }))
            }
            components={{ ValueContainer: CustomDropdownContainer }}
            {...selectMenuProps}
          />
          <FormFieldLabel>Eligibility</FormFieldLabel>
          <Select
            className="multi-select mb-2"
            classNamePrefix="react-select"
            options={ELIGIBILITY_OPTIONS}
            value={ELIGIBILITY_OPTIONS.find((o) => o.value === form.eligibility_type)}
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, eligibility_type: opt?.value || "both" }))
            }
            components={{ ValueContainer: CustomDropdownContainer }}
            {...selectMenuProps}
          />
          <FormFieldLabel>Workflow</FormFieldLabel>
          <Select
            className="multi-select mb-2"
            classNamePrefix="react-select"
            options={WORKFLOW_OPTIONS}
            value={WORKFLOW_OPTIONS.find((o) => o.value === form.workflow_type)}
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, workflow_type: opt?.value || "one_level" }))
            }
            components={{ ValueContainer: CustomDropdownContainer }}
            {...selectMenuProps}
          />
          <Form.Check
            type="switch"
            id="policy-auto"
            label="Auto Generate"
            checked={!!form.auto_generate}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, auto_generate: e.target.checked }))
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModal(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={!form.appraisal_type_id}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <DeleteModal
        show={!!deleteId}
        onDelete={confirmDelete}
        onHide={() => setDeleteId(null)}
      />
    </div>
  );
};

export default PafEnginePolicies;
